import { AnimatePresence, motion } from 'motion/react';
import { lazy, Suspense, useEffect } from 'react';

import { RobinLogoSkeleton } from '@/shared/ui/robin-logo-skeleton';
import { ChatPanelState, useChatActions, useChatId, useChatPanelState, useChatSelectedCandidate } from '../../lib';
import { useStyles } from './Page.styles';
import { PageBackground } from './page-background';

const Chat = lazy(() => import('../chat').then((module) => ({ default: module.Chat })));
const JobPreview = lazy(() => import('../job-preview').then((module) => ({ default: module.JobPreview })));
const BestMatches = lazy(() => import('../best-matches').then((module) => ({ default: module.BestMatches })));
const CandidateDetailsAdapter = lazy(() =>
  import('../candidate-details-adapter').then((module) => ({ default: module.CandidateDetailsAdapter })),
);

const RightPanelContent = ({ children }: { children: React.ReactNode }) => {
  const classes = useStyles();

  return (
    <motion.div
      css={classes.rightPanelContent}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, position: 'absolute' }}
      transition={{ duration: 0.33, ease: 'circIn' }}>
      <Suspense fallback={<RobinLogoSkeleton position="center" />}>{children}</Suspense>
    </motion.div>
  );
};

const RightPanel = () => {
  const classes = useStyles();

  const chatPanelState = useChatPanelState();
  const { setChatPanelState } = useChatActions();

  return (
    <AnimatePresence>
      {chatPanelState !== ChatPanelState.Closed && (
        <motion.div
          css={classes.rightPanel}
          initial={{ width: '0%', marginLeft: 0 }}
          animate={{ width: '100%', marginLeft: 24 }}
          exit={{ width: '0%', marginLeft: 0 }}
          transition={{ duration: 0.33 }}>
          <AnimatePresence>
            {chatPanelState === ChatPanelState.JobPreview && (
              <RightPanelContent key={ChatPanelState.JobPreview}>
                <JobPreview onClose={() => setChatPanelState(ChatPanelState.Closed)} />
              </RightPanelContent>
            )}
            {chatPanelState === ChatPanelState.BestMatches && (
              <RightPanelContent key={ChatPanelState.BestMatches}>
                <BestMatches onClose={() => setChatPanelState(ChatPanelState.Closed)} />
              </RightPanelContent>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ChatWrapper = () => {
  const classes = useStyles();
  const selectedCandidate = useChatSelectedCandidate();

  return (
    <AnimatePresence initial={false}>
      {!selectedCandidate ? (
        <motion.div
          css={classes.wrapper}
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: '100%' }}
          exit={{ opacity: 0, width: 0 }}>
          <AnimatePresence>
            <Chat />
          </AnimatePresence>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const CandidateDetailsWrapper = () => {
  const classes = useStyles();
  const selectedCandidate = useChatSelectedCandidate();

  return (
    <AnimatePresence>
      {selectedCandidate ? (
        <motion.div
          css={classes.wrapper}
          initial={{ opacity: 0, width: 0, marginLeft: 0 }}
          animate={{ opacity: 1, width: '100%', marginLeft: 24 }}
          exit={{ opacity: 0, width: 0, marginLeft: 0 }}>
          <AnimatePresence>
            <CandidateDetailsAdapter />
          </AnimatePresence>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export const PageLazy = () => {
  const classes = useStyles();
  const chatId = useChatId();
  const { reset } = useChatActions();

  useEffect(() => {
    reset();
  }, [chatId, reset]);

  return (
    <Suspense fallback={<RobinLogoSkeleton />}>
      <div key={chatId} css={classes.content}>
        <div css={classes.background}>
          <PageBackground />
        </div>

        <div css={classes.foreground}>
          <Suspense fallback={<RobinLogoSkeleton position="center" />}>
            <ChatWrapper />
          </Suspense>

          <Suspense fallback={<RobinLogoSkeleton position="center" />}>
            <RightPanel />
          </Suspense>

          <Suspense fallback={<RobinLogoSkeleton position="center" />}>
            <CandidateDetailsWrapper />
          </Suspense>
        </div>
      </div>
    </Suspense>
  );
};
